import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import useAuthStore from '../store/useAuthStore';
import { PermissionUtils } from '../utils/permissions';
import { getSafeReturnUrl } from '../utils/returnUrl';
import type { CurrentUser } from '../services/authService';

interface ProtectedRouteProps {
  /**
   * When provided, wraps children (leaf route).
   * When omitted, renders <Outlet /> (layout route for nested children).
   */
  children?: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  requiredRoles?: string[];
  customCheck?: (user: CurrentUser | null) => boolean;
}

/**
 * Route guard for authentication and optional role checks.
 *
 * Preferred usage: layout composition
 * - Require auth on the app shell parent
 * - Require admin / super-admin on pathless layout groups with <Outlet />
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requiredRoles = [],
  customCheck,
}) => {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated || !user) {
    const returnUrl = getSafeReturnUrl(location.pathname + location.search);
    const search = returnUrl
      ? `?returnUrl=${encodeURIComponent(returnUrl)}`
      : '';
    return <Navigate to={`/login${search}`} replace />;
  }

  let hasPermission = true;

  if (customCheck) {
    hasPermission = customCheck(user);
  } else if (requireSuperAdmin) {
    hasPermission = PermissionUtils.isSuperAdmin(user);
  } else if (requireAdmin) {
    hasPermission = PermissionUtils.isAdmin(user);
  } else if (requiredRoles.length > 0) {
    hasPermission = PermissionUtils.hasAnyRole(user, requiredRoles);
  }

  if (!hasPermission) {
    return (
      <Result
        status="403"
        title="权限不足"
        subTitle="抱歉，您没有权限访问此页面。请联系管理员获取相应权限。"
        extra={
          <Button type="primary" onClick={() => window.history.back()}>
            返回上一页
          </Button>
        }
      />
    );
  }

  // Layout mode (pathless parent): render nested routes via Outlet.
  // Leaf mode: render explicit children.
  return children != null ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
